import {useMemo, useState} from 'react';
import {Link, useLocation} from 'react-router-dom';
import {Search, X} from 'lucide-react';
import {cn} from '@/matic/lib/utils/cn';
import {useQuoteRequests} from '../hooks/useQuoting';
import {
  dayGroup,
  elapsedMs,
  OPEN_STATUSES,
  quoteStatus,
  quoteTitle,
  STATUS_LABEL,
  type QuoteStatus,
} from './quote-status';
import {StatusTimeBox} from './StatusTimeBox';
import {QueueSortToggle} from './QueueSortToggle';
import {useQueueSort} from './quote-sort-store';

type Filter = 'all' | 'open' | 'done';

const FILTERS: Array<[Filter, string]> = [
  ['all', 'All'],
  ['open', 'Open'],
  ['done', 'Done'],
];

type Row = {
  id: string;
  title: string;
  subject: string;
  status: QuoteStatus;
  ms: number | null;
  day: string;
  haystack: string;
};

/**
 * Every inbound quote on one line each in the rail, newest first under day headers,
 * with the status box on the right: the whole queue and what is late, at a glance.
 * Hosted by `LibraryRail` under the AES white-label; the rows link to the workspace.
 */
export function QuoteSidebarList() {
  const requests = useQuoteRequests();
  const {pathname} = useLocation();
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  const sort = useQueueSort((state) => state.sort);
  const activeId = pathname.startsWith('/quotes/')
    ? decodeURIComponent(pathname.slice('/quotes/'.length))
    : undefined;

  const rows = useMemo<Row[]>(() => {
    const now = Date.now();
    return (requests.data ?? [])
      .map((item) => ({
        id: item.id,
        title: quoteTitle(item),
        subject: item.subject,
        status: quoteStatus(item),
        ms: elapsedMs(item.receivedAt, now),
        day: dayGroup(item.receivedAt, now),
        // Everything a person might type to find it: customer, engine, subject, request id.
        haystack: [
          item.customer,
          item.engineKey,
          item.kw,
          item.subject,
          item.id,
        ]
          .filter((part) => part !== null && part !== undefined)
          .join(' ')
          .toLowerCase(),
      }))
      .sort((a, b) =>
        sort === 'newest'
          ? (a.ms ?? Infinity) - (b.ms ?? Infinity)
          : (b.ms ?? -1) - (a.ms ?? -1)
      );
  }, [requests.data, sort]);

  // Every word typed must appear somewhere in the quote; the status filter still applies.
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  const shown = rows.filter(
    (row) =>
      (filter === 'all'
        ? true
        : filter === 'open'
          ? OPEN_STATUSES.has(row.status)
          : !OPEN_STATUSES.has(row.status)) &&
      terms.every((term) => row.haystack.includes(term))
  );

  let lastDay: string | undefined;

  return (
    <div className="mb-4 flex flex-col">
      <label className="mx-1 mt-1 flex h-8 items-center gap-2 rounded-md bg-white/[0.07] px-2 text-sidebar-fg/60 focus-within:bg-white/[0.12] focus-within:text-white">
        <Search className="h-3.5 w-3.5 shrink-0" />
        <input
          type="search"
          value={query}
          placeholder="Search quotes"
          aria-label="Search quotes by customer, engine or subject"
          onChange={(event) => {
            setQuery(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') setQuery('');
          }}
          className="min-w-0 flex-1 bg-transparent text-[13px] text-white outline-none placeholder:text-sidebar-fg/40 [&::-webkit-search-cancel-button]:hidden"
        />
        {query && (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => {
              setQuery('');
            }}
            className="rounded p-0.5 hover:bg-white/10"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </label>
      <div className="flex items-center justify-between px-2.5 pb-1 pt-2">
        {/* The list head is the order, not a label: the rail is self-evidently the queue. */}
        <QueueSortToggle variant="dark" />
        <span className="flex gap-0.5">
          {FILTERS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => {
                setFilter(key);
              }}
              className={cn(
                'rounded px-1.5 py-0.5 text-[10.5px] font-semibold uppercase tracking-wide transition-colors hover:text-white',
                filter === key
                  ? 'bg-neutral-100 text-sidebar-bg hover:text-sidebar-bg'
                  : 'text-white/60'
              )}
            >
              {label}
            </button>
          ))}
        </span>
      </div>

      {requests.isLoading && (
        <div className="px-2.5 py-1.5 text-[11px] text-sidebar-fg/40">
          Loading the queue…
        </div>
      )}
      {requests.isError && (
        <div className="px-2.5 py-1.5 text-[11px] text-error-400">
          Couldn't load the queue
        </div>
      )}
      {!requests.isLoading && !requests.isError && shown.length === 0 && (
        <div className="px-2.5 py-1.5 text-[11px] text-sidebar-fg/40">
          {query ? `No quote matches “${query}”` : 'Nothing here'}
        </div>
      )}

      <nav className="flex flex-col gap-0.5" aria-label="Quotes">
        {shown.map((row) => {
          const header = row.day === lastDay ? null : row.day;
          lastDay = row.day;
          const active = row.id === activeId;
          const tooltip = `${STATUS_LABEL[row.status]} · ${row.subject}`;
          return (
            <div key={row.id} className="flex flex-col gap-0.5">
              {header && (
                <div className="px-2.5 pb-0.5 pt-2 text-[11px] text-sidebar-fg/40">
                  {header}
                </div>
              )}
              <Link
                to={`/quotes/${encodeURIComponent(row.id)}`}
                title={tooltip}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 rounded px-2.5 py-1.5 no-underline transition-colors',
                  active
                    ? 'bg-sidebar-active text-white'
                    : 'hover:bg-sidebar-hover',
                  OPEN_STATUSES.has(row.status)
                    ? active
                      ? 'text-white'
                      : 'text-sidebar-fg/90'
                    : 'text-success-400'
                )}
              >
                <span className="truncate text-[13px] font-medium">
                  {row.title}
                </span>
                <StatusTimeBox status={row.status} elapsedMs={row.ms} />
              </Link>
            </div>
          );
        })}
      </nav>
    </div>
  );
}
